/**
 * The public level file, and the one validator every reader uses (GEN-003).
 *
 * Lives in `sim` because all three readers depend on it and on nothing else in
 * common: `levelgen` writes and re-reads it, the web client fetches it (Phase 3), and
 * the Worker serves and replays against it (Phase 4). Level JSON is untrusted input
 * wherever it is read (PRD §5): anything not listed here is dropped, and anything out
 * of range throws.
 *
 * Format: PRD §7, with one deliberate difference. There is no `puzzleNumber`; numbers
 * are assigned by the daily system from the launch date (Phase 3), so a level file does
 * not change if the launch date does (Phase 2 plan §8, Q2).
 *
 * The reference solution is NEVER part of this file (N7). It lives in a separate,
 * private file that is not committed and not published.
 */
import { MAX_BALLS_FOR_SIZE } from './deal.ts';
import { GENERATOR_VERSION, PHYSICS_VERSION } from './version.ts';
import { DEFAULT_PHYSICS } from './config.ts';
import { createTableSpec, type TableSizeFt } from './table.ts';
import type { Layout } from './types.ts';

export interface Level {
  /** Same as `dateKey`: one level per day. */
  readonly id: string;
  /** `YYYY-MM-DD` in Melbourne time (PRD D-003). */
  readonly dateKey: string;
  /**
   * What `generateLayout` was seeded with: derived from the secret seed key (GEN-006), so
   * it reveals nothing about any other day.
   */
  readonly seed: string;
  readonly generatorVersion: number;
  readonly physicsVersion: number;
  readonly table: {
    readonly sizeFt: TableSizeFt;
    readonly width: number;
    readonly height: number;
    readonly pocketScale: number;
  };
  readonly cue: { readonly x: number; readonly y: number };
  readonly balls: readonly { readonly id: number; readonly x: number; readonly y: number }[];
  /** The solver's best findable line (Phase 2 plan §3, PRD D-020). */
  readonly par: number;
  readonly rules: { readonly variant: 'all-equal' };
}

const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

function fail(what: string): never {
  throw new Error(`parseLevel: ${what}`);
}

function record(value: unknown, what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    fail(`${what} must be an object`);
  return value as Record<string, unknown>;
}

function finite(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(`${what} must be a finite number`);
  return value;
}

function positiveInteger(value: unknown, what: string): number {
  const n = finite(value, what);
  if (!Number.isInteger(n) || n < 1) fail(`${what} must be a whole number ≥ 1`);
  return n;
}

function dateKey(value: unknown): string {
  if (typeof value !== 'string') fail('dateKey must be a string');
  const m = DATE_KEY.exec(value);
  const month = Number(m?.[2]);
  const day = Number(m?.[3]);
  if (m === null || month < 1 || month > 12 || day < 1 || day > 31) {
    fail(`dateKey must be YYYY-MM-DD, got "${value}"`);
  }
  return value;
}

/** Validate anything into a `Level`, or throw with the first thing wrong with it. */
export function parseLevel(raw: unknown): Level {
  const l = record(raw, 'a level');

  const key = dateKey(l['dateKey']);
  if (l['id'] !== key) fail('id must equal dateKey');
  if (typeof l['seed'] !== 'string' || l['seed'] === '' || l['seed'].length > 64) {
    fail('seed must be a short non-empty string');
  }

  const t = record(l['table'], 'table');
  const sizeFt = t['sizeFt'];
  if (sizeFt !== 7 && sizeFt !== 8 && sizeFt !== 9) fail('table.sizeFt must be 7, 8 or 9');
  // The geometry is derived from the size, never trusted from the file: a level whose
  // numbers disagree with its own size is a corrupt level, not a new kind of table.
  const spec = createTableSpec(sizeFt, DEFAULT_PHYSICS.pocketScale);
  if (finite(t['width'], 'table.width') !== spec.widthM) fail('table.width does not match sizeFt');
  if (finite(t['height'], 'table.height') !== spec.heightM)
    fail('table.height does not match sizeFt');
  if (finite(t['pocketScale'], 'table.pocketScale') !== spec.pocketScale) {
    fail('table.pocketScale does not match this physics');
  }

  const onTable = (value: unknown, what: string): { x: number; y: number } => {
    const p = record(value, what);
    const x = finite(p['x'], `${what}.x`);
    const y = finite(p['y'], `${what}.y`);
    if (x < 0 || x > spec.widthM || y < 0 || y > spec.heightM) fail(`${what} is off the table`);
    return { x, y };
  };

  const cue = onTable(l['cue'], 'cue');
  if (!Array.isArray(l['balls'])) fail('balls must be an array');
  const rawBalls: unknown[] = l['balls'];
  if (rawBalls.length < 1 || rawBalls.length > MAX_BALLS_FOR_SIZE[sizeFt]) {
    fail(`a ${String(sizeFt)}ft table takes 1–${String(MAX_BALLS_FOR_SIZE[sizeFt])} balls`);
  }
  const balls = rawBalls.map((value, i) => {
    const b = record(value, `balls[${String(i)}]`);
    if (b['id'] !== i + 1) fail(`balls[${String(i)}].id must be ${String(i + 1)}`);
    return { id: i + 1, ...onTable(b, `balls[${String(i)}]`) };
  });

  const rules = record(l['rules'], 'rules');
  if (rules['variant'] !== 'all-equal') fail('rules.variant must be "all-equal"');

  return {
    id: key,
    dateKey: key,
    seed: l['seed'],
    generatorVersion: positiveInteger(l['generatorVersion'], 'generatorVersion'),
    physicsVersion: positiveInteger(l['physicsVersion'], 'physicsVersion'),
    table: { sizeFt, width: spec.widthM, height: spec.heightM, pocketScale: spec.pocketScale },
    cue,
    balls,
    par: positiveInteger(l['par'], 'par'),
    rules: { variant: 'all-equal' },
  };
}

/** The table a level describes, as the game and the solver play it. */
export function levelToLayout(level: Level): Layout {
  return {
    cue: { xM: level.cue.x, yM: level.cue.y },
    balls: level.balls.map((ball) => ({ xM: ball.x, yM: ball.y })),
  };
}

/**
 * True if this level can be played in this build: solved under the same physics, so
 * every shot replays exactly as it was checked (N10). The generator version is only a
 * record of how the table was made; a newer generator for later days must never make an
 * already-published day unplayable (GEN-010). Used wherever a level is served or played.
 */
export function levelIsPlayable(level: Level): boolean {
  return level.physicsVersion === PHYSICS_VERSION;
}

/**
 * True if this level was made by the physics AND generator in this build: the level
 * generator's test for "already done" (levelgen pipeline), never for playing.
 */
export function levelIsCurrent(level: Level): boolean {
  return level.physicsVersion === PHYSICS_VERSION && level.generatorVersion === GENERATOR_VERSION;
}

/**
 * A short fingerprint of the table a level lays out: its size and where every ball
 * starts (GEN-012). Stored beside each run, so a run started on a table that was later
 * changed (the owner regenerating a published day, D-052) is never continued, counted
 * or shown as a run on the new one. Par is left out on purpose: re-solving par does not
 * change the table, so a run on it is still good.
 *
 * FNV-1a, 32 bits, over a fixed text form. Integer arithmetic on a string, so every
 * engine gives the same answer (ARCHITECTURE.md §5.5). Not a secret and not a security
 * check: it only has to tell two versions of one day apart.
 */
export function levelTableKey(level: Level): string {
  const text = [
    String(level.table.sizeFt),
    `${String(level.cue.x)},${String(level.cue.y)}`,
    ...level.balls.map((b) => `${String(b.id)}:${String(b.x)},${String(b.y)}`),
  ].join('|');
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash = Math.imul(hash ^ text.charCodeAt(i), 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
